import { ReactElement, useEffect, useState } from 'react';
import { alpha, Box, useMediaQuery } from '@mui/material';
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Props as DotProps } from 'recharts/types/shape/Dot';

import { IRatioChartData } from 'modules/analytics/actions/getRatioChartData';
import { RangeSelectorType } from 'modules/api/const';

import { ChartTooltipContent } from '../ChartTooltipContent';
import {
  ActiveDot,
  IActiveDotProps,
} from '../StakingChart/components/ActiveDot';
import { Cursor } from '../StakingChart/components/Cursor';
import { formatDate, getMainDays } from '../StakingChart/utils';
import { CHART_HEIGHT, TICK_PADDING } from './const';
import { useRatioChart } from './useRatioChart';
import { useStyles } from './useStyles';

interface Props {
  className?: string;
  data: IRatioChartData;
  range: RangeSelectorType;
}

export function RatioChart({ className, data, range }: Props) {
  const { cx, classes, theme } = useStyles();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));

  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 10);
    return () => {
      setVisible(false);
      clearTimeout(t);
    };
  }, []);

  const { chartData, monthTickTimestamps, sixMonthsTickTimestamps } =
    useRatioChart(data, range);

  return (
    <Box
      className={cx(classes.root, className)}
      sx={{
        opacity: visible ? 1 : 0,
      }}
    >
      <ResponsiveContainer
        className={classes.container}
        height={CHART_HEIGHT}
        width="100%"
      >
        <AreaChart data={chartData} margin={{ top: 26 }}>
          <XAxis
            dataKey="timestamp"
            domain={['dataMin', 'dataMax']}
            interval={0}
            scale="time"
            stroke="none"
            tick={
              ((props: IActiveDotProps & DotProps) => {
                const { x, y, payload } = props;
                const point = chartData.find(
                  p => p.timestamp === payload?.value,
                );

                if (isSm) {
                  return null;
                }

                if (!point || point.isEdge) {
                  return null;
                }

                if (range === RangeSelectorType.Month) {
                  if (!monthTickTimestamps.includes(point.timestamp)) {
                    return null;
                  }
                }

                if (range === RangeSelectorType.SixMonths) {
                  if (!sixMonthsTickTimestamps.includes(point.timestamp)) {
                    return null;
                  }
                }

                return (
                  <text
                    fill={alpha(theme.palette.secondary.contrastText, 0.6)}
                    fontSize={12}
                    textAnchor="middle"
                    x={x}
                    y={
                      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                      Number(y) + TICK_PADDING
                    }
                  >
                    {formatDate(point.date, range)}
                  </text>
                );
              }) as unknown as ReactElement<SVGElement>
            }
            tickCount={getMainDays(range) + 2}
            type="number"
          />

          <YAxis hide domain={['dataMin - 0.01', 'dataMax + 0.01']} />

          <Tooltip
            content={<ChartTooltipContent decimals={4} postfix="IOTA" />}
            cursor={<Cursor dataKey="value" />}
            formatter={(v: number) =>
              `${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
            }
            labelFormatter={ts => formatDate(new Date(Number(ts)), range)}
          />

          <defs>
            <linearGradient
              id="paint0_linear_10149_77284"
              x1="0"
              x2="0"
              y1="0"
              y2="1"
            >
              <stop
                offset="5%"
                stopColor={theme.palette.primary.main}
                stopOpacity={0.2}
              />

              <stop
                offset="95%"
                stopColor={theme.palette.primary.main}
                stopOpacity={0}
              />
            </linearGradient>
          </defs>

          <Area
            connectNulls
            activeDot={<ActiveDot />}
            dataKey="value"
            fill="url(#paint0_linear_10149_77284)"
            fillOpacity={1}
            stroke={theme.palette.primary.main}
            strokeWidth="2px"
            type="monotone"
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
}
